const EQUIPMENT_LABELS = {
  fiber_housing_2u: 'FIBER CONNECTOR HOUSING',
  fiber_housing_4u: 'FIBER SPLICE HOUSING',
  router:           'ROUTER',
  switch:           'NETWORK SWITCH',
  ciena:            'CIENA DEVICE',
  sel:              'SEL RELAY',
  patch_panel:      'PATCH PANEL',
  cable_mgmt:       'CABLE MANAGEMENT',
  amplifier:        'AMPLIFIER',
  empty:            'BLANK',
};

function normalizeSnapshot(fmp) {
  const raw = fmp?.rack_snapshot;
  if (!raw) return null;
  if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
  return raw;
}

// Build a diff between current equipment and previous snapshot equipment
function buildDiff(currentEquipment, previousEquipment) {
  if (!previousEquipment || !previousEquipment.length) return {};
  const diff = {};
  const prevById = Object.fromEntries(previousEquipment.map(e => [e.id, e]));
  const currById = Object.fromEntries(currentEquipment.map(e => [e.id, e]));

  // Added — in current but not in previous
  currentEquipment.forEach(eq => {
    if (!prevById[eq.id]) diff[eq.id] = { status: 'added', eq };
    else if (prevById[eq.id].rack_unit_start !== eq.rack_unit_start)
      diff[eq.id] = { status: 'moved', eq, from_unit: prevById[eq.id].rack_unit_start };
    else diff[eq.id] = { status: 'unchanged', eq };
  });

  // Removed — in previous but not in current
  previousEquipment.forEach(eq => {
    if (!currById[eq.id]) diff[`removed_${eq.id}`] = { status: 'removed', eq };
  });

  return diff;
}

const CHANGE_STYLES = {
  added:     { border: '2px solid #22c55e', backgroundColor: '#f0fdf4', tag: '▲ ADDED',   tagColor: '#166534', tagBg: '#bbf7d0' },
  removed:   { border: '2px dashed #ef4444', backgroundColor: '#fef2f2', tag: '✕ REMOVED', tagColor: '#991b1b', tagBg: '#fecaca' },
  moved:     { border: '2px solid #f59e0b', backgroundColor: '#fffbeb', tag: '→ MOVED',   tagColor: '#92400e', tagBg: '#fde68a' },
  unchanged: { border: '1px solid black',   backgroundColor: 'white',   tag: null },
};

// ── Engineering Rack Elevation ─────────────────────────────────
function RackElevation({ snapshot, rackLabel }) {
  if (!snapshot?.rack) {
    return (
      <div style={{ border: '1px solid black', padding: 24, textAlign: 'center', color: '#666' }}>
        No rack snapshot — marked as no physical change.
      </div>
    );
  }

  const rack      = snapshot.rack;
  const equipment = snapshot.equipment || [];
  const totalU    = rack.total_units || 42;

  // Build diff if previous snapshot exists
  const prevEquipment = snapshot.previous_snapshot
    ? (typeof snapshot.previous_snapshot === 'string'
        ? JSON.parse(snapshot.previous_snapshot).equipment
        : snapshot.previous_snapshot.equipment)
    : null;
  const diff = buildDiff(equipment, prevEquipment);
  const hasDiff = prevEquipment && Object.keys(diff).some(k => diff[k].status !== 'unchanged');

  // Collect removed items to show as ghost slots
  const removedItems = prevEquipment
    ? prevEquipment.filter(e => !equipment.find(c => c.id === e.id))
    : [];

  // Build slot map top-to-bottom (U1 at top) — matches RackView
  const slots = [];
  const occupied = new Set();

  for (let u = 1; u <= totalU; u++) {
    if (occupied.has(u)) { slots.push({ u, skip: true }); continue; }
    const eq = equipment.find(e => u >= e.rack_unit_start && u < e.rack_unit_start + e.rack_unit_height);
    if (eq) {
      for (let i = 0; i < eq.rack_unit_height; i++) occupied.add(eq.rack_unit_start + i);
      slots.push({ u, eq, span: eq.rack_unit_height });
    } else {
      slots.push({ u, eq: null, span: 1 });
    }
  }

  // Top-to-bottom — same direction as RackView
  const rows = slots.filter(s => !s.skip);

  const unitPx = 22; // pixels per 1U

  return (
    <div style={{ fontFamily: 'Arial, sans-serif' }}>
      {/* Rack title bar */}
      <div style={{ border: '2px solid black', borderBottom: '1px solid black', padding: '4px 8px', textAlign: 'center', fontWeight: 'bold', fontSize: 13, letterSpacing: '0.08em', backgroundColor: '#f0f0f0' }}>
        {(rack.label || rackLabel || 'RACK').toUpperCase()}
      </div>

      {/* Rack body */}
      <div style={{ display: 'flex', border: '2px solid black', borderTop: 'none' }}>

        {/* Height scale (left) */}
        <div style={{ width: 36, borderRight: '1px solid black', flexShrink: 0, position: 'relative' }}>
          {rows.map((slot, i) => {
            const h = slot.span * unitPx;
            // Show height label every ~12U
            const showLabel = slot.u % 12 === 0 || slot.u === 1 || slot.u === totalU;
            const inches = Math.round((totalU - slot.u + 1) * 1.75);
            return (
              <div key={slot.u} style={{ height: h, borderBottom: '1px solid #ccc', display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end', paddingRight: 3, boxSizing: 'border-box' }}>
                {showLabel && <span style={{ fontSize: 8, color: '#333', lineHeight: 1 }}>{inches}"</span>}
              </div>
            );
          })}
        </div>

        {/* U numbers */}
        <div style={{ width: 28, borderRight: '1px solid black', flexShrink: 0 }}>
          {rows.map(slot => (
            <div key={slot.u} style={{ height: slot.span * unitPx, borderBottom: '1px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
              <span style={{ fontSize: 9, color: '#333', fontFamily: 'Arial, sans-serif' }}>{slot.u}</span>
            </div>
          ))}
        </div>

        {/* Equipment column */}
        <div style={{ flex: 1, borderRight: '1px solid black' }}>
          {rows.map(slot => {
            const h = slot.span * unitPx;
            if (!slot.eq) {
              // Check if a removed item used to be at this U position
              const removed = removedItems.find(r => r.rack_unit_start === slot.u);
              if (removed) {
                const cs = CHANGE_STYLES.removed;
                return (
                  <div key={slot.u} style={{ height: removed.rack_unit_height * unitPx, borderBottom: '1px solid black', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2px 8px', boxSizing: 'border-box', backgroundColor: cs.backgroundColor, border: cs.border }}>
                    <div style={{ display: 'inline-block', backgroundColor: cs.tagBg, color: cs.tagColor, fontSize: 8, fontWeight: 'bold', padding: '1px 5px', borderRadius: 3, marginBottom: 2 }}>{cs.tag}</div>
                    <span style={{ fontSize: Math.max(8, Math.min(11, removed.rack_unit_height * unitPx / 3)), fontWeight: 'bold', textAlign: 'center', color: '#991b1b', letterSpacing: '0.04em' }}>{removed.label?.toUpperCase()}</span>
                    <span style={{ fontSize: 8, color: '#991b1b' }}>WAS AT U{removed.rack_unit_start}</span>
                  </div>
                );
              }
              return (
                <div key={slot.u} style={{ height: h, borderBottom: '1px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
                  <span style={{ fontSize: 9, color: '#aaa', letterSpacing: '0.05em' }}>SPACE</span>
                </div>
              );
            }
            const eq = slot.eq;
            const label = eq.label ? eq.label.toUpperCase() : (EQUIPMENT_LABELS[eq.equipment_type] || eq.equipment_type?.toUpperCase() || 'EQUIPMENT');
            const subLabel = EQUIPMENT_LABELS[eq.equipment_type] || eq.equipment_type?.replace(/_/g,' ').toUpperCase() || '';
            const change = diff[eq.id] || { status: 'unchanged' };
            const cs = CHANGE_STYLES[change.status] || CHANGE_STYLES.unchanged;
            return (
              <div key={slot.u} style={{ height: h, borderBottom: '1px solid black', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2px 8px', boxSizing: 'border-box', backgroundColor: cs.backgroundColor, outline: cs.border }}>
                {cs.tag && <div style={{ display: 'inline-block', backgroundColor: cs.tagBg, color: cs.tagColor, fontSize: 8, fontWeight: 'bold', padding: '1px 5px', borderRadius: 3, marginBottom: 2 }}>
                  {cs.tag}{change.status === 'moved' ? ` FROM U${change.from_unit}` : ''}
                </div>}
                <span style={{ fontSize: Math.max(8, Math.min(12, h / 3)), fontWeight: 'bold', textAlign: 'center', letterSpacing: '0.04em', lineHeight: 1.2 }}>{label}</span>
                {subLabel && subLabel !== label && h > 28 && (
                  <span style={{ fontSize: 8, color: '#555', textAlign: 'center', marginTop: 2 }}>{subLabel}</span>
                )}
              </div>
            );
          })}
        </div>

        {/* Right tick marks (screw holes / vent indicators) */}
        <div style={{ width: 18, flexShrink: 0 }}>
          {rows.map(slot => (
            <div key={slot.u} style={{ height: slot.span * unitPx, borderBottom: '1px solid #ccc', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-around', boxSizing: 'border-box' }}>
              {Array.from({ length: Math.min(slot.span * 2, 4) }).map((_, i) => (
                <div key={i} style={{ width: 10, height: 1, backgroundColor: '#999' }} />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Bottom label */}
      <div style={{ border: '2px solid black', borderTop: '1px solid black', padding: '3px 8px', textAlign: 'center', fontSize: 9, color: '#333', backgroundColor: '#f0f0f0' }}>
        FRONT VIEW &nbsp;&nbsp; {totalU}U RACK
      </div>

      {/* Change legend */}
      {hasDiff && (
        <div style={{ border: '1px solid #ccc', borderTop: 'none', padding: '6px 10px', display: 'flex', gap: 16, flexWrap: 'wrap', backgroundColor: '#fafafa' }}>
          <span style={{ fontSize: 9, fontWeight: 'bold', color: '#333', marginRight: 4 }}>CHANGE LEGEND:</span>
          {[['added','▲ ADDED','#bbf7d0','#166534'],['moved','→ MOVED','#fde68a','#92400e'],['removed','✕ REMOVED','#fecaca','#991b1b']].map(([,tag,bg,color]) => (
            <span key={tag} style={{ fontSize: 9, backgroundColor: bg, color, padding: '1px 6px', borderRadius: 3, fontWeight: 'bold' }}>{tag}</span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Acknowledgment Stamp ───────────────────────────────────────
function AcknowledgmentStamp({ fmp }) {
  if (!fmp.acknowledged_by_name && !fmp.verified_by_name) return null;
  const name = fmp.acknowledged_by_name || fmp.verified_by_name;
  const at   = fmp.acknowledged_at || fmp.verified_at;
  return (
    <div style={{ border: '3px solid #16a34a', borderRadius: 6, padding: '10px 16px', margin: '0 auto 12px', maxWidth: 640, display: 'flex', alignItems: 'center', gap: 14, backgroundColor: '#f0fdf4' }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid #16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, backgroundColor: 'white' }}>
        <span style={{ fontSize: 22, color: '#16a34a', fontWeight: 'bold' }}>✓</span>
      </div>
      <div>
        <div style={{ fontWeight: 'bold', fontSize: 13, color: '#14532d', letterSpacing: '0.05em' }}>ACCURACY ACKNOWLEDGED</div>
        <div style={{ fontSize: 11, color: '#166534', marginTop: 2 }}>
          Field Engineer: <strong>{name}</strong>
        </div>
        {at && <div style={{ fontSize: 10, color: '#4ade80', marginTop: 1, color: '#166534' }}>{new Date(at).toLocaleString()}</div>}
        <div style={{ fontSize: 9, color: '#4d7c0f', marginTop: 3, fontStyle: 'italic' }}>
          I certify that the information contained in this Field Markup Print is accurate and reflects the current physical state of the rack.
        </div>
      </div>
    </div>
  );
}

// ── Title Block ────────────────────────────────────────────────
function TitleBlock({ fmp, snapshot, generatedAt }) {
  const rack = snapshot?.rack;
  return (
    <div style={{ border: '2px solid black', borderTop: '1px solid black', display: 'grid', gridTemplateColumns: '1fr 2fr', fontFamily: 'Arial, sans-serif', fontSize: 10 }}>

      {/* Left — revision table */}
      <div style={{ borderRight: '1px solid black' }}>
        <div style={{ borderBottom: '1px solid black', display: 'grid', gridTemplateColumns: '1fr 1fr 3fr 1fr', textAlign: 'center' }}>
          {['DATE','NO','DESCRIPTION','APPVD'].map(h => (
            <div key={h} style={{ padding: '2px 4px', borderRight: '1px solid black', fontWeight: 'bold', fontSize: 9 }}>{h}</div>
          ))}
        </div>
        {[0,1,2].map(i => (
          <div key={i} style={{ borderBottom: '1px solid #ccc', display: 'grid', gridTemplateColumns: '1fr 1fr 3fr 1fr', height: 18 }}>
            {[0,1,2,3].map(j => <div key={j} style={{ borderRight: '1px solid #ccc' }} />)}
          </div>
        ))}
        <div style={{ padding: '4px 6px', color: '#666', fontSize: 8, borderTop: '1px solid black' }}>
          COMPUTER GENERATED // MANUAL CHANGES NOT PERMITTED
        </div>
      </div>

      {/* Right — project info */}
      <div>
        <div style={{ borderBottom: '1px solid black', padding: '3px 8px', fontWeight: 'bold', fontSize: 12, textAlign: 'center', letterSpacing: '0.05em' }}>
          FIELD MARKUP PRINT (FMP)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid black' }}>
          {[
            ['LOCATION', fmp.location_name || '—'],
            ['RACK',     rack?.label || fmp.rack_label || '—'],
            ['WO #',     fmp.wo_number || '—'],
            ['PO #',     fmp.po_number || '—'],
            ['SN TICKET',fmp.sn_ticket || '—'],
            ['FMP TYPE', (fmp.fmp_type || 'rack_view').replace(/_/g,' ').toUpperCase()],
            ['DATE',     new Date(generatedAt).toLocaleDateString()],
            ['GENERATED BY', fmp.generated_by_name || '—'],
          ].map(([label, value]) => (
            <div key={label} style={{ padding: '3px 8px', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', display: 'flex', gap: 6 }}>
              <span style={{ fontWeight: 'bold', color: '#444', minWidth: 70 }}>{label}:</span>
              <span>{value}</span>
            </div>
          ))}
        </div>
        {fmp.change_summary && (
          <div style={{ padding: '4px 8px', borderBottom: '1px solid #ccc' }}>
            <span style={{ fontWeight: 'bold', color: '#444' }}>CHANGE SUMMARY: </span>
            <span>{fmp.change_summary}</span>
          </div>
        )}
        <div style={{ padding: '3px 8px', display: 'flex', justifyContent: 'space-between' }}>
          <span style={{ color: '#555' }}>FieldCore — Field Markup Print System</span>
          <span style={{ fontWeight: 'bold' }}>REV: {new Date(generatedAt).toISOString().slice(0,10).replace(/-/g,'')}</span>
        </div>
      </div>
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────
export default function FMPPreviewModal({ fmp, onClose, onSubmit, submitting }) {
  if (!fmp) return null;
  const snapshot    = normalizeSnapshot(fmp);
  const generatedAt = fmp.generated_at || snapshot?.captured_at || new Date().toISOString();

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 5000, backgroundColor: 'rgba(15,23,42,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          .fmp-print, .fmp-print * { visibility: visible !important; }
          .fmp-print {
            position: fixed !important;
            inset: 0 !important;
            width: 100vw !important;
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            background: white !important;
            padding: 12px !important;
          }
          .fmp-no-print { display: none !important; }
          .fmp-scroll { overflow: visible !important; max-height: none !important; }
        }
        @page { margin: 0.5in; size: letter portrait; }
      `}</style>

      <div className="fmp-print" style={{ width: 'min(820px,100%)', maxHeight: '92vh', backgroundColor: 'white', borderRadius: 8, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: 'Arial, sans-serif' }}>

        {/* Screen header — hidden on print */}
        <div className="fmp-no-print" style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1e293b', color: 'white', borderRadius: '8px 8px 0 0', flexShrink: 0 }}>
          <div>
            <div style={{ fontWeight: 'bold', fontSize: 15 }}>FMP Preview</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{fmp.location_name} · {snapshot?.rack?.label || fmp.rack_label} · {(fmp.fmp_type||'rack_view').replace(/_/g,' ')}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Drawing area */}
        <div className="fmp-scroll" style={{ flex: 1, overflowY: 'auto', padding: 20, backgroundColor: 'white' }}>
          <div style={{ maxWidth: 640, margin: '0 auto' }}>
            <RackElevation snapshot={snapshot} rackLabel={fmp.rack_label} />
            <AcknowledgmentStamp fmp={fmp} />
            <TitleBlock fmp={fmp} snapshot={snapshot} generatedAt={generatedAt} />
          </div>
        </div>

        {/* Action buttons — hidden on print */}
        <div className="fmp-no-print" style={{ padding: '10px 16px', borderTop: '1px solid #e2e8f0', display: 'flex', gap: 8, justifyContent: 'flex-end', backgroundColor: '#f8fafc', borderRadius: '0 0 8px 8px', flexShrink: 0 }}>
          <button onClick={() => window.print()} style={{ padding: '8px 14px', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: 7, cursor: 'pointer', fontSize: 13 }}>
            🖨️ Print / Export PDF
          </button>
          {onSubmit && (
            <button onClick={onSubmit} disabled={submitting} style={{ padding: '8px 16px', backgroundColor: submitting ? '#94a3b8' : '#7c3aed', color: 'white', border: 'none', borderRadius: 7, cursor: submitting ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 'bold' }}>
              {submitting ? 'Submitting...' : 'Submit for FE Verification'}
            </button>
          )}
          <button onClick={onClose} style={{ padding: '8px 14px', backgroundColor: '#e2e8f0', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 13, color: '#334155' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
